import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import '../styles/components/Sidenav.scss';
import logo from '../assets/vivvy-logo.png';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import StoreIcon from '@mui/icons-material/Store'; // Import store icon
import CloseIcon from '@mui/icons-material/Close'; // Import close icon for mobile
import { supabase } from '../helpers/Supabase';
import { useHistory } from 'react-router-dom';
interface SidenavProps {
    toggleSidenav: () => void;
    isCollapsed: boolean;
    isMobileMenuOpen: boolean;
    closeMobileMenu: () => void;
    isMobile: boolean;
}

interface UserPermissions {
    dashboard: boolean;
    settings: boolean;
    stores: boolean;
    reports: boolean;
    chatbot: boolean;
}

const Sidenav: React.FC<SidenavProps> = ({ 
    toggleSidenav, 
    isCollapsed, 
    isMobileMenuOpen, 
    closeMobileMenu, 
    isMobile 
}) => {
    const history = useHistory();
    const [userPermissions, setUserPermissions] = useState<UserPermissions>({
        dashboard: true,
        settings: true,
        stores: true,
        reports: true,
        chatbot: true
    });
    const [, setIsDelegate] = useState(false);

    useEffect(() => {
        const fetchUserPermissions = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            
            if (user) {
                // Check if user is a delegate
                const { data: clientDelegate } = await supabase
                    .from('client_delegates')
                    .select('*')
                    .eq('email', user.email)
                    .eq('is_active', true)
                    .single();

                const { data: storeDelegate } = await supabase
                    .from('store_delegates')
                    .select('*')
                    .eq('email', user.email)
                    .eq('is_active', true)
                    .single();

                if (clientDelegate) {
                    setIsDelegate(true);
                    setUserPermissions(clientDelegate.permissions);
                } else if (storeDelegate) {
                    setIsDelegate(true);
                    setUserPermissions(storeDelegate.permissions);
                } else {
                    // Regular client user - full permissions
                    setIsDelegate(false);
                    setUserPermissions({
                        dashboard: true,
                        settings: true,
                        stores: true,
                        reports: true,
                        chatbot: true
                    });
                }
            }
        };

        fetchUserPermissions();
    }, []);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        history.push('/');
        if (isMobile) {
            closeMobileMenu();
        }
    };

    const handleNavClick = () => {
        if (isMobile) {
            closeMobileMenu();
        }
    };

    return (
        <>
            {/* Mobile overlay */}
            {isMobile && isMobileMenuOpen && (
                <div className="mobile-overlay" onClick={closeMobileMenu}></div>
            )}
            
            <div className={`sidenav ${isCollapsed ? 'collapsed' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''} ${isMobile ? 'mobile' : ''}`}>
                <div className="sidenav-header">
                    <img src={logo} alt="Vivvy Logo" className="logo fade-in" onClick={isMobile ? undefined : toggleSidenav} />
                    {isMobile && (
                        <button className="close-mobile-menu" onClick={closeMobileMenu} aria-label="Close menu">
                            <CloseIcon />
                        </button>
                    )}
                </div>
                
                <nav className="sidenav-links">
                    {userPermissions.dashboard && (
                        <NavLink to="/dashboard" className="link" activeClassName="active" onClick={handleNavClick}>
                            <HomeIcon className="icon" />
                            <span className="text">Home & Insights</span>
                        </NavLink>
                    )}
                    
                    {userPermissions.chatbot && (
                        <NavLink to="/chatbot-setup" className="link" activeClassName="active" onClick={handleNavClick}>
                            <ChatIcon className="icon" />
                            <span className="text">Chatbot Setup</span>
                        </NavLink>
                    )}
                    
                    <NavLink to="/qr-code" className="link" activeClassName="active" onClick={handleNavClick}>
                        <QrCodeIcon className="icon" />
                        <span className="text">QR Display Page Settings</span>
                    </NavLink>
                    
                    {userPermissions.stores && (
                        <NavLink to="/store-manager" className="link" activeClassName="active" onClick={handleNavClick}>
                            <StoreIcon className="icon" />
                            <span className="text">Store Manager</span>
                        </NavLink>
                    )}
                </nav>
                
                <div className="sidenav-bottom-links">
                    {userPermissions.settings && (
                        <NavLink to="/settings" className="link" activeClassName="active" onClick={handleNavClick}>
                            <SettingsIcon className="icon" />
                            <span className="text">Settings</span>
                        </NavLink>
                    )}
                    <button className="link" onClick={handleLogout}>
                        <ExitToAppIcon className="icon" />
                        <span className="text">Logout</span>
                    </button>
                </div>
            </div>
        </>
    );
};

export default Sidenav;